import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import {
    NgApexchartsModule,
    ApexOptions,
    ChartType,
    ApexDataLabels,
    ApexChart,
    ApexFill,
    ApexPlotOptions,
} from 'ng-apexcharts';
import ApexCharts from 'apexcharts';
import { FormattingPipe } from 'app/modules/shared/services/formatting-data.pipe';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ColorPickerModule } from 'ngx-color-picker';
import { MatButtonModule } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { Overlay } from '@angular/cdk/overlay';
import { MatSelectSearchComponent } from '../mat-select-search/mat-select-search.component';
import {
    generateColorVariants,
    aggregatePieData,
    resolveDefaultSeriesSelection,
    reorderChartDataByMonth,
    makeSeriesNamer,
    prettifyColumnName,
    deriveGroupByFromQuery,
    resolveTrackerThreshold,
    resolveTooltipLayout,
    shouldFollowTooltipCursor,
    collectTooltipRows,
    buildSharedTooltipHtml,
} from './chart-render.util';
import { ChartToolbarOverflowDirective } from './chart-toolbar-overflow.directive';
// Responsive toolbar: overflow icons collapse into the 3-dot menu.

// Re-exported for existing importers (e.g. chat-ai conversation component).
export { generateColorVariants } from './chart-render.util';

export type FromContext =
    | 'dashboard'
    | 'Analytics'
    | 'Tracker'
    | 'report'
    | ''
    | string;

export interface PinnedPoint {
    dataPointIndex: number;
    category: string;
    rawCategory: string;
    values: { name: string; value: string; color: string }[];
}

type Nullable<T> = T | null | undefined;

export interface ChartSeriesEntry {
    name: string;
    data: Array<number | null>;
    type?: ChartType;
    originalType?: ChartType;
}

export type ChartSeriesMap = Record<string, ChartSeriesEntry[]>;

export interface ChartData {
    id: string;
    xAxis?: string;
    yAxis?: string;
    labels: any[];
    series: ChartSeriesMap | boolean;
    color?: string;
    title?: string;
    threshold?: number | string;
    thresholdType?: string;
    appliedFilters?: any[];
    subActivityId?: string;
    trackerId?: string;
    /** Column the series are grouped by (labels the series selector). */
    groupBy?: string;
    /** SQL for the tracker query (fallback source for `groupBy`). */
    query?: string;
    dateColumn?: string;
}

@Component({
    selector: 'app-dynamic-charts',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        NgApexchartsModule,
        MatButtonToggleModule,
        MatIconModule,
        MatTooltipModule,
        MatMenuModule,
        ColorPickerModule,
        MatButtonModule,
        MatSelectModule,
        MatOptionModule,
        MatFormFieldModule,
        MatInputModule,
        TranslocoModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ReactiveFormsModule,
        MatSelectSearchComponent,
        ChartToolbarOverflowDirective,
    ],
    templateUrl: './dynamic-charts.component.html',
    styleUrls: ['./dynamic-charts.component.scss'],
    providers: [FormattingPipe],
})
export class DynamicChartsComponent
    implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
    // ---------- Inputs / Outputs (kept intact) ----------
    @Input() chartData!: ChartData;
    @Input() chartTitle!: Nullable<string>;
    @Input() currentIndex!: Nullable<string>;
    @Input() isEditMode!: Nullable<boolean>;
    @Input() secondLevelFrom!: Nullable<string>;
    @Input() from: FromContext = '';
    @Input() type?: string = '';
    @Input() isFull?: boolean = false;
    @Input() previousSelectedSeriesList?: string[] = [];
    @Input() initialSortOrder: 'asc' | 'desc' | null = null;
    /** Optional delay (ms) before first init to stagger many charts and reduce lag. */
    @Input() initDelayMs = 0;
    /** When true, Apex init waits until the host enters (or nears) the viewport. */
    @Input() deferInit = false;
    /** When true, no Apex chart title (parent supplies heading, e.g. message chart dialog). */
    @Input() hideTitle = false;
    /** Set from tracker API for delete permission (manager/staff need this true). */
    @Input() isMyTracker?: boolean | null;
    /** When true, tracker share toolbar action is shown (owner + not in report-selection mode). */
    @Input() showShareTracker = false;
    /** Highlights the share icon when the tracker already has recipients. */
    @Input() isTrackerShared?: boolean | null;
    /** Chart canvas height in px. Consumers with height-constrained tiles (e.g. dashboard) pass a viewport-fitting value. */
    @Input() chartHeight = 400;

    @Output() expandChartEvent = new EventEmitter<any>();
    @Output() addToPage = new EventEmitter<any>();
    @Output() deleteChartEvent = new EventEmitter<any>();
    @Output() filterChartEvent = new EventEmitter<any>();
    @Output() openTrackerSettings = new EventEmitter<any>();
    @Output() openTrackerDataTable = new EventEmitter<any>();
    @Output() shareTrackerEvent = new EventEmitter<MouseEvent>();
    @Output() changeTrackerColor = new EventEmitter<string>();
    @Output() dateRangeChange = new EventEmitter<{
        startDate: Date | null;
        endDate: Date | null;
    }>();
    @Output() sortChange = new EventEmitter<'asc' | 'desc' | null>();
    @Output() chartRendered = new EventEmitter<void>();

    @ViewChild('chartTypeMenuTrigger') chartTypeMenuTrigger!: MatMenuTrigger;
    @ViewChild('colorPickerInput', { read: ElementRef })
    colorPickerInput!: ElementRef<HTMLElement>;
    @ViewChild('chartWrapper') chartWrapper!: HTMLElement;
    @ViewChild('apexToolbarHost', { read: ElementRef })
    apexToolbarHost!: ElementRef<HTMLElement>;

    /** Delete in toolbar: Tracker only - admin always; manager/staff only if `isMyTracker`. Other contexts unchanged. */
    get showTrackerDeleteToolbar(): boolean {
        if (this.from !== 'Tracker') {
            return true;
        }
        if (typeof localStorage === 'undefined') {
            return false;
        }
        const role = localStorage.getItem('userRole') ?? '';
        if (role === 'admin' || role === 'company') {
            return true;
        }
        if (role === 'manager' || role === 'staff') {
            return this.isMyTracker === true;
        }
        return true;
    }

    // ---------- internals ----------
    private readonly destroyed$ = new Subject<void>();
    /** Interactive ("zoom-lock") mode. A click on the chart turns it on; leaving
     * the chart (mouse-leave) turns it off. While on, the wheel zooms the chart
     * and page scroll is suppressed; while off, the wheel scrolls the page. */
    interactiveMode = false;
    private hovering = false;
    // Pointer tracking so a pan (down → move → up) is never treated as a click
    // that pins a point.
    private pointerDownX = 0;
    private pointerDownY = 0;
    private pointerDidDrag = false;
    // ApexCharts context + full x-axis data bounds, used by wheel-zoom.
    private apexCtx: any = null;
    private dataMinX = NaN;
    private dataMaxX = NaN;

    public chartOptions: ApexOptions = {};
    public currentSeriesIndex = 0;
    public seriesNames: string[] = [];
    /** Prettified group-by column name; labels the multi-series selector. */
    public seriesGroupByLabel = '';
    public selectedSeriesList: string[] = [];
    /** FormControl for mat-select-search series selector (synced with selectedSeriesList). */
    seriesSelectControl = new FormControl<string[]>([]);
    public currentChartType: ChartType = 'line';
    public tempColor = '';
    public showToolbar = true;
    public isDataLabelsVisible = false;
    public areLabelsNumericOrDate = false;
    public isChartFullscreen = false;

    // UI helpers
    selectAllValue = '__all__';
    isAllSelected = false;

    private baseLabels: any[] = [];
    private baseSeriesMap: ChartSeriesMap = {};
    private isSorted = false;
    private chartRenderedEmitted = false;
    /** Only one Apex chart builds at a time app-wide — prevents main-thread spikes on tracker. */
    private static initQueue: DynamicChartsComponent[] = [];
    private static initRunning = false;
    private static readonly INIT_GAP_MS = 120;
    private static readonly TRACKER_INIT_GAP_MS = 320;
    private initQueued = false;

    /** True while any Apex chart is queued or building — tracker page yields UI work. */
    static isInitActive(): boolean {
        return (
            DynamicChartsComponent.initRunning ||
            DynamicChartsComponent.initQueue.length > 0
        );
    }
    private sortedCategories: any[] | null = null;
    public sortOrder: 'asc' | 'desc' | null = null;
    private hiddenSeries = new Set<string>();
    private fullscreenRafId: number | null = null;
    private fullscreenResizeTimer: ReturnType<typeof setTimeout> | null = null;
    private preFullscreenChartHeight: number | string | undefined;
    private preFullscreenApexOptionsSnapshot: {
        chartZoom: any;
        chartToolbar: any;
        legend: any;
    } | null = null;
    private static fullscreenRequestChartId: string | null = null;

    /** Watches `body` for the app's dark/light class flip. ApexCharts bakes the
     * theme (donut center label, slice strokes, gradient shade, legend colors)
     * into the options at build time, so a live toggle leaves those UI elements
     * stuck in the previous scheme until the chart is rebuilt. */
    private themeObserver: MutationObserver | null = null;
    private deferInitObserver: IntersectionObserver | null = null;
    private chartInitAllowed = false;
    private initDelayTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly hostEl = inject(ElementRef<HTMLElement>);
    private readonly zone = inject(NgZone);
    private handleThemeChange = (): void => {
        if (!this.chartOptions || Object.keys(this.chartOptions).length === 0) {
            return;
        }
        const expectedMode = this.isDarkMode ? 'dark' : 'light';
        const currentMode = this.chartOptions?.theme?.mode;
        if (currentMode !== undefined && currentMode !== expectedMode) {
            // Force a full rebuild so every theme-derived visual (not just
            // theme.mode) is regenerated for the new scheme.
            this.memoKey = null;
            this.initializeChart();
            this._cdr.markForCheck();
        }
    };

    private fullscreenChangeHandler = () => {
        if (this.fullscreenRafId != null) {
            cancelAnimationFrame(this.fullscreenRafId);
        }
        this.fullscreenRafId = requestAnimationFrame(() => {
            const active = this.getDocumentFullscreenElement();
            if (!active) {
                DynamicChartsComponent.fullscreenRequestChartId = null;
                if (this.isChartFullscreen) {
                    this.isChartFullscreen = false;
                    this.applyFullscreenChartHeight(false, 0);
                }
                this._cdr.markForCheck();
                return;
            }
            if (!this.isThisInstanceFullscreenElement(active)) {
                if (this.isChartFullscreen) {
                    this.isChartFullscreen = false;
                    const h = this.getFullscreenHost();
                    if (h) {
                        h.classList.remove('is-fullscreen');
                    }
                }
                this._cdr.markForCheck();
                return;
            }
            if (!this.isChartFullscreen) {
                this.isChartFullscreen = true;
            }
            this.applyFullscreenChartHeight(true, 0);
            this._cdr.markForCheck();
        });
    };

    private isThisInstanceFullscreenElement(active: HTMLElement): boolean {
        const host = this.getFullscreenHost();
        if (!host || !active) {
            return false;
        }
        if (active === host) {
            return true;
        }
        if (active === document.documentElement) {
            const myId = this.chartData?.id;
            if (!myId || !DynamicChartsComponent.fullscreenRequestChartId) {
                return false;
            }
            return String(myId) === DynamicChartsComponent.fullscreenRequestChartId;
        }
        return false;
    }

    // Pinned-points state
    private _pinnedMap = new Map<number, PinnedPoint>();
    pinnedPointsArray: PinnedPoint[] = [];
    highlightedPointIndex: number | null = null;

    /* Initialized at construction: ngOnChanges (first chart build) runs before
       ngOnInit, so these must never be undefined when generateColorVariants runs. */
    c1Value: string = getComputedStyle(document.documentElement).getPropertyValue('--c1').trim() || '#3dc3fb';
    c2Value: string = getComputedStyle(document.documentElement).getPropertyValue('--c2').trim() || '#49e8ff';

    /**
     * Layout applies `body.dark` asynchronously after the first change detection cycle.
     * Charts that init in ngOnInit would otherwise read a false here and keep ApexCharts in light theme.
     * Prefer body class; if not set yet, match app bootstrap / classy layout (localStorage `theme`).
     */
    private get isDarkMode(): boolean {
        if (typeof document === 'undefined') {
            return false;
        }
        const body = document.body;
        if (body.classList.contains('dark')) {
            return true;
        }
        if (body.classList.contains('light')) {
            return false;
        }
        try {
            return localStorage.getItem('theme') === 'dark';
        } catch {
            return false;
        }
    }

    // report date controls
    showReportDatePicker = false;
    reportStartDate: Date | null = null;
    reportEndDate: Date | null = null;

    // memoization key
    private memoKey: string | null = null;
    matTooltipDisabled: boolean = false;

    private readonly _transloco = inject(TranslocoService);

    constructor(
        private _cdr: ChangeDetectorRef,
        private formattingPipe: FormattingPipe,
        private overlay: Overlay,
    ) {}

    // ---------- Lifecycle ----------
    ngOnInit(): void {
        this.c1Value = getComputedStyle(document.documentElement).getPropertyValue('--c1').trim() || this.c1Value;
        this.c2Value = getComputedStyle(document.documentElement).getPropertyValue('--c2').trim() || this.c2Value;
        document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
        this.themeObserver = new MutationObserver(this.handleThemeChange);
        this.themeObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });
        if (this.deferInit) {
            return;
        }
        this.chartInitAllowed = true;
        this.scheduleChartInit();
    }

    private scheduleChartInit(): void {
        if (this.initDelayTimer != null) {
            clearTimeout(this.initDelayTimer);
            this.initDelayTimer = null;
        }
        if (this.initDelayMs > 0) {
            this.initDelayTimer = setTimeout(() => {
                this.initDelayTimer = null;
                this.initializeChart();
            }, this.initDelayMs);
            return;
        }
        this.initializeChart();
    }

    private setupDeferInitObserver(): void {
        if (!this.deferInit || this.chartInitAllowed) {
            return;
        }
        this.zone.runOutsideAngular(() => {
            this.deferInitObserver?.disconnect();
            this.deferInitObserver = new IntersectionObserver(
                (entries) => {
                    if (!entries.some(e => e.isIntersecting)) {
                        return;
                    }
                    this.deferInitObserver?.disconnect();
                    this.deferInitObserver = null;
                    this.chartInitAllowed = true;
                    this.zone.run(() => {
                        this.scheduleChartInit();
                        this.runPostViewChartSetup();
                    });
                },
                { rootMargin: '0px', threshold: 0.05 },
            );
            this.deferInitObserver.observe(this.hostEl.nativeElement);
        });
    }

    ngAfterViewInit(): void {
        this.setupDeferInitObserver();
        if (!this.deferInit || this.chartInitAllowed) {
            this.runPostViewChartSetup();
        }
    }

    /** Deferred sort/theme reconcile — skipped until deferInit allows chart init. */
    private runPostViewChartSetup(): void {
        setTimeout(() => {
            if (
                this.chartOptions &&
                Object.keys(this.chartOptions).length > 0
            ) {
                this.chartOptions = { ...this.chartOptions };
                this._cdr.markForCheck();
            }

            if (this.initialSortOrder && this.selectedSeriesList?.length) {
                this.sortSeries(this.initialSortOrder);
            }

            const expectedMode = this.isDarkMode ? 'dark' : 'light';
            const currentMode = this.chartOptions?.theme?.mode;
            if (
                this.chartOptions &&
                Object.keys(this.chartOptions).length > 0 &&
                currentMode !== undefined &&
                currentMode !== expectedMode
            ) {
                this.memoKey = null;
                this.initializeChart();
            }
        }, 80);
    }

    private fixApexToolbar(chartEl: HTMLElement): void {
        const toolbar = chartEl.querySelector(
            '.apexcharts-toolbar',
        ) as HTMLElement | null;
        if (!toolbar) return;

        const hasRealTools =
            toolbar.querySelector('.apexcharts-zoom-in-icon') ||
            toolbar.querySelector('.apexcharts-zoom-out-icon') ||
            toolbar.querySelector('.apexcharts-reset-icon') ||
            toolbar.querySelector('.apexcharts-pan-icon');

        toolbar.classList.remove('apex-toolbar-hidden', 'apex-toolbar-visible');
        if (!hasRealTools) {
            toolbar.classList.add('apex-toolbar-hidden');
        } else {
            toolbar.classList.add('apex-toolbar-visible');
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['previousSelectedSeriesList'] &&
            !changes['previousSelectedSeriesList'].firstChange
        ) {
            const curr = changes['previousSelectedSeriesList']
                .currentValue as string[];
            this.selectedSeriesList = Array.isArray(curr) ? [...curr] : [];
            this.syncSeriesSelectControl();
            this.onMultiSeriesChange(this.selectedSeriesList);
        }

        if (changes['chartData'] && this.chartData) {
            if (!this.chartData.series || !this.chartData.labels) {
                console.warn(
                    'DynamicCharts ngOnChanges: Missing series or labels',
                    this.chartData,
                );
                return;
            }

            // Safeguard: month axes must render in calendar order even if the
            // payload arrived alphabetically sorted (see chart-render.util).
            this.chartData = reorderChartDataByMonth(this.chartData);

            // Label the multi-series selector with the group-by column. Prefer
            // the backend `groupBy`; fall back to parsing the SQL for charts
            // persisted before the field was surfaced.
            this.seriesGroupByLabel = prettifyColumnName(
                this.chartData.groupBy ||
                    deriveGroupByFromQuery(
                        this.chartData.query,
                        this.chartData.dateColumn,
                    ),
            );

            // When stagger/defer is used, let the scheduled init run on first load.
            if (
                (this.initDelayMs > 0 || this.deferInit) &&
                changes['chartData'].firstChange &&
                !this.chartInitAllowed
            ) {
                this.hiddenSeries.clear();
                if (this.chartData.id) {
                    const baseId =
                        String(this.chartData.id).split('-')[0] ||
                        String(this.chartData.id);
                    this.chartData = {
                        ...this.chartData,
                        id: `${baseId}-${Date.now()}`,
                    };
                }
                this._cdr.markForCheck();
                return;
            }

            // destroy previous chart instance if needed
            const oldChartId = this.chartOptions?.chart?.['id'];
            if (oldChartId) {
                try {
                    ApexCharts.exec(String(oldChartId), 'destroy');
                } catch {}
            }

            this.hiddenSeries.clear();
            // ensure unique id to allow re-render
            if (this.chartData.id) {
                const baseId =
                    String(this.chartData.id).split('-')[0] ||
                    String(this.chartData.id);
                this.chartData = {
                    ...this.chartData,
                    id: `${baseId}-${Date.now()}`,
                };
            }

            this.initializeChart();
            this._cdr.markForCheck();

            if (this.initialSortOrder) {
                setTimeout(() => {
                    if (this.selectedSeriesList?.length) {
                        this.sortSeries(this.initialSortOrder);
                    }
                }, 100);
            }
        } else if (changes['chartTitle'] || changes['hideTitle']) {
            if (this.chartOptions) {
                this.updateOptions({
                    title:
                        this.hideTitle || this.from === 'report'
                            ? undefined
                            : this.buildTitleOptions(),
                });
            }
        }
    }
    private updateOptions(partial: Partial<ApexOptions>): void {
        this.chartOptions = {
            ...this.chartOptions,
            ...partial,
        };
        this._cdr.markForCheck();
    }
    private sanitizeColor(value: any): string {
        const fallback = '#3498db';
        if (!value) return fallback;

        if (typeof value === 'string') {
            const c = value.startsWith('#') ? value : `#${value}`;
            return /^#[0-9A-Fa-f]{6}$/.test(c) ? c : fallback;
        }

        return fallback;
    }

    /** Captures the full x-axis data bounds (stable across zoom/pan) so
     * wheel-zoom can clamp to them. `seriesX` holds every point's x value even
     * when the view is zoomed, so its min/max is the true data range. */
    private captureXBounds(chart: any): void {
        try {
            const g = chart?.w?.globals;
            const xs = (g?.seriesX && g.seriesX[0]) || [];
            if (xs.length) {
                this.dataMinX = xs.reduce(
                    (m: number, v: number) => Math.min(m, v),
                    Infinity,
                );
                this.dataMaxX = xs.reduce(
                    (m: number, v: number) => Math.max(m, v),
                    -Infinity,
                );
            } else if (g) {
                this.dataMinX = g.minX;
                this.dataMaxX = g.maxX;
            }
        } catch {
            /* ignore */
        }
    }

    /** Zooms the x-axis toward the cursor on each wheel tick while interactive
     * mode is active. Reads the current view from ApexCharts globals (which
     * reflect any pan/zoom already applied) and clamps to the data bounds. */
    private zoomByWheel(ev: WheelEvent, el: HTMLElement): void {
        const id = String(this.chartData?.id ?? '');
        const g = this.apexCtx?.w?.globals;
        if (!id || !g) return;
        const dataMin = this.dataMinX;
        const dataMax = this.dataMaxX;
        const viewMin = g.minX;
        const viewMax = g.maxX;
        if (
            ![dataMin, dataMax, viewMin, viewMax].every(Number.isFinite) ||
            dataMax <= dataMin
        ) {
            return;
        }

        const grid = el.querySelector(
            '.apexcharts-grid',
        ) as HTMLElement | null;
        const rect = (grid ?? el).getBoundingClientRect();
        const frac =
            rect.width > 0
                ? Math.min(
                      1,
                      Math.max(0, (ev.clientX - rect.left) / rect.width),
                  )
                : 0.5;
        const cursorX = viewMin + frac * (viewMax - viewMin);

        const fullSpan = dataMax - dataMin;
        const minSpan = Math.max(fullSpan / 30, Number.EPSILON);
        const factor = ev.deltaY < 0 ? 0.82 : 1.22;
        let span = (viewMax - viewMin) * factor;
        span = Math.max(minSpan, Math.min(fullSpan, span));

        let newMin = cursorX - frac * span;
        let newMax = cursorX + (1 - frac) * span;
        if (newMin < dataMin) {
            newMin = dataMin;
            newMax = dataMin + span;
        }
        if (newMax > dataMax) {
            newMax = dataMax;
            newMin = dataMax - span;
        }

        try {
            ApexCharts.exec(id, 'zoomX', newMin, newMax);
        } catch {
            /* ignore */
        }
    }

    ngOnDestroy(): void {
        document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
        this.themeObserver?.disconnect();
        this.themeObserver = null;
        this.deferInitObserver?.disconnect();
        this.deferInitObserver = null;
        if (this.initQueued) {
            DynamicChartsComponent.initQueue =
                DynamicChartsComponent.initQueue.filter(c => c !== this);
            this.initQueued = false;
            if (DynamicChartsComponent.initRunning) {
                DynamicChartsComponent.initRunning = false;
                DynamicChartsComponent.pumpInitQueue();
            }
        }
        if (this.initDelayTimer != null) {
            clearTimeout(this.initDelayTimer);
            this.initDelayTimer = null;
        }
        if (this.fullscreenRafId != null) {
            cancelAnimationFrame(this.fullscreenRafId);
            this.fullscreenRafId = null;
        }
        if (this.fullscreenResizeTimer != null) {
            clearTimeout(this.fullscreenResizeTimer);
            this.fullscreenResizeTimer = null;
        }
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    async toggleBrowserFullscreen(): Promise<void> {
        const host = this.getFullscreenHost();
        if (!host) return;

        const active = this.getDocumentFullscreenElement();
        const doc: any = document;
        if (active) {
            const exit =
                doc.exitFullscreen ||
                doc.webkitExitFullscreen ||
                doc.msExitFullscreen;
            if (exit) {
                await Promise.resolve(exit.call(doc));
            }
            return;
        }

        this.preFullscreenChartHeight =
            this.chartOptions?.chart?.height ?? this.chartHeight;
        const ch0 = this.chartOptions?.chart as any;
        const leg0 = this.chartOptions?.legend;
        this.preFullscreenApexOptionsSnapshot = {
            chartZoom: ch0?.zoom
                ? JSON.parse(JSON.stringify(ch0.zoom))
                : null,
            chartToolbar: ch0?.toolbar
                ? JSON.parse(JSON.stringify(ch0.toolbar))
                : null,
            legend: leg0
                ? JSON.parse(JSON.stringify(leg0))
                : null,
        };
        DynamicChartsComponent.fullscreenRequestChartId = this.chartData?.id
            ? String(this.chartData.id)
            : null;

        const requestHost =
            (host as any).requestFullscreen ||
            (host as any).webkitRequestFullscreen ||
            (host as any).msRequestFullscreen;
        try {
            if (requestHost) {
                await Promise.resolve(requestHost.call(host));
                return;
            }
        } catch { }

        const root: any = document.documentElement;
        const requestRoot =
            root.requestFullscreen ||
            root.webkitRequestFullscreen ||
            root.msRequestFullscreen;
        if (requestRoot) {
            await Promise.resolve(requestRoot.call(root));
        }
    }

    private getFullscreenHost(): HTMLElement | null {
        const maybeHost: any = (this.chartWrapper as any)?.nativeElement ?? this.chartWrapper;
        return maybeHost instanceof HTMLElement ? maybeHost : null;
    }

    private getDocumentFullscreenElement(): HTMLElement | null {
        const doc: any = document;
        return (doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement || null) as HTMLElement | null;
    }

    private applyFullscreenChartHeight(
        isFullscreen: boolean,
        delayOverride?: number
    ): void {
        const host = this.getFullscreenHost();
        if (host) {
            host.classList.toggle('is-fullscreen', isFullscreen);
        }
        const chartId = this.chartOptions?.chart?.['id'];
        if (!chartId) {
            return;
        }
        if (this.fullscreenResizeTimer != null) {
            clearTimeout(this.fullscreenResizeTimer);
        }
        const delayMs =
            delayOverride ?? (isFullscreen ? 50 : 100);
        this.fullscreenResizeTimer = setTimeout(() => {
            this.fullscreenResizeTimer = null;
            const id = String(chartId);
            const targetHeight = isFullscreen
                ? Math.max(window.innerHeight - 24, 320)
                : (this.preFullscreenChartHeight ?? 400);
            const ch = this.chartOptions?.chart as any;
            const snap = this.preFullscreenApexOptionsSnapshot;
            // Normalize 'area' (the actual ApexCharts render type for line-mode
            // charts, see toRenderType/fromRenderType) back to 'line' so the
            // zoom/toolbar branching below still recognizes it.
            const chartType = this.fromRenderType(
                (ch?.type || this.currentChartType) as ChartType,
            ) as string;
            let updatePayload: { chart: any; legend?: any };
            if (isFullscreen) {
                const s = this.preFullscreenApexOptionsSnapshot;
                let nextToolbar: any;
                if (chartType === 'pie') {
                    nextToolbar = {
                        ...ch?.toolbar,
                        tools: {
                            download:
                                ch?.toolbar?.tools?.download !== false,
                            reset: false,
                        },
                    };
                } else if (ch?.toolbar && ch.toolbar.show === false) {
                    nextToolbar = ch.toolbar;
                } else {
                    nextToolbar = {
                        ...ch?.toolbar,
                        show: ch?.toolbar?.show,
                        autoSelected: 'pan',
                        tools: {
                            download: true,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: true,
                        },
                    };
                }
                const nextChart: any = { height: targetHeight, toolbar: nextToolbar };
                if (s?.chartZoom) {
                    nextChart.zoom = { ...s.chartZoom, enabled: false };
                } else if (chartType === 'line' || chartType === 'bar') {
                    nextChart.zoom = ch?.zoom
                        ? { ...ch.zoom, enabled: false }
                        : { enabled: false, type: 'x', autoScaleYaxis: true };
                }
                const leg = {
                    ...((this.chartOptions as any).legend || {}),
                    onItemClick: { toggleDataSeries: false },
                    onItemHover: { highlightDataSeries: false },
                };
                updatePayload = {
                    chart: nextChart,
                    legend: leg,
                };
            } else {
                const chartOut: any = { height: targetHeight };
                if (snap) {
                    if (snap.chartZoom) {
                        chartOut.zoom = snap.chartZoom;
                    }
                    if (snap.chartToolbar) {
                        chartOut.toolbar = snap.chartToolbar;
                    }
                } else {
                    if (ch?.zoom) {
                        chartOut.zoom = { ...ch.zoom };
                    }
                    if (ch?.toolbar) {
                        chartOut.toolbar = { ...ch.toolbar };
                    }
                }
                updatePayload = { chart: chartOut };
                if (snap?.legend) {
                    updatePayload.legend = snap.legend;
                }
                this.preFullscreenApexOptionsSnapshot = null;
            }
            try {
                ApexCharts.exec(
                    id,
                    'updateOptions',
                    updatePayload,
                    true,
                    false
                );
            } catch { }
            if (isFullscreen) {
                this.chartOptions = {
                    ...this.chartOptions,
                    chart: { ...ch, ...updatePayload.chart } as any,
                    legend: updatePayload.legend as any,
                } as any;
            } else {
                const chNext = { ...ch, ...updatePayload.chart } as any;
                if (
                    snap &&
                    !snap.chartZoom &&
                    chartType === 'pie' &&
                    (ch as any).zoom
                ) {
                    delete chNext.zoom;
                }
                this.chartOptions = {
                    ...this.chartOptions,
                    chart: chNext,
                    ...(updatePayload.legend != null
                        ? { legend: updatePayload.legend }
                        : {}),
                } as any;
            }
            this._cdr.markForCheck();
        }, delayMs);
    }

    // ---------- Helpers / normalization (kept but efficient) ----------
    private normalizeSeriesKeys(
        series: ChartSeriesMap | boolean | undefined,
    ): ChartSeriesMap {
        if (!series || series === true) return {};
        const norm: ChartSeriesMap = {};
        Object.keys(series).forEach((k) => {
            norm[k] = series[k].map(s => ({ ...s }));
        });
        return norm;
    }

    /** Maps lowercase series keys to the first-seen label casing from the API (for UI / select options). */
    private buildSeriesKeyDisplayByLower(
        series: ChartSeriesMap | boolean | undefined,
    ): Record<string, string> {
        if (!series || series === true) return {};
        const map: Record<string, string> = {};
        for (const k of Object.keys(series)) {
            const lk = k;
            if (map[lk] === undefined) {
                map[lk] = k;
            }
        }
        return map;
    }

    private ensureSeriesFromRawFlags(): void {
        const labelsFlag =
            typeof (this.chartData as any)?.labels === 'boolean'
                ? (this.chartData as any).labels
                : false;
        if (this.chartData?.series === true && labelsFlag) {
            const xAxisKey = this.chartData.xAxis || 'x';
            const yAxisKey = this.chartData.yAxis || 'y';
            const labels =
                (this.chartData as any).data?.map(
                    (entry: any) => entry?.[xAxisKey]?.t,
                ) ?? [];
            const seriesData =
                (this.chartData as any).data?.map((entry: any) =>
                    this.coerceNumberOrNull(entry?.[yAxisKey]),
                ) ?? [];
            this.chartData.series = {
                default: [{ name: yAxisKey, type: 'line', data: seriesData }],
            } as ChartSeriesMap;
            this.chartData.labels = labels;
        }
    }

    private coerceNumberOrNull(v: any): number | null {
        if (v === '' || v === undefined || v === null) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }

    /** Coerce series data so string "NaN" and invalid numbers become null before chart/tooltip use. */
    private coerceSeriesEntries(
        series: ChartSeriesEntry[],
    ): ChartSeriesEntry[] {
        return series.map(s => ({
            ...s,
            data: Array.isArray(s?.data)
                ? s.data.map(v => this.coerceNumberOrNull(v))
                : [],
        }));
    }

    private getVisibleSeriesNames(map: ChartSeriesMap): string[] {
        const keys = Object.keys(map);
        const visible = keys.filter(k => k !== 'default');
        return visible.length ? visible : keys;
    }

    private cleanSelection(
        sel: string[],
        visibleDisplayKeys: string[],
    ): string[] {
        const byLower = new Map(visibleDisplayKeys.map(k => [k, k]));
        return (sel ?? [])
            .map(k => byLower.get(String(k ?? '')))
            .filter((k): k is string => k != null);
    }

    // ---------- Initialization / rebuild (simplified) ----------
    initializeChart(): void {
        if (this.deferInit && !this.chartInitAllowed) {
            return;
        }
        if (this.initQueued) {
            return;
        }
        this.initQueued = true;
        DynamicChartsComponent.initQueue.push(this);
        DynamicChartsComponent.pumpInitQueue();
    }

    private static pumpInitQueue(): void {
        if (DynamicChartsComponent.initRunning || !DynamicChartsComponent.initQueue.length) {
            return;
        }
        DynamicChartsComponent.initRunning = true;
        const comp = DynamicChartsComponent.initQueue.shift()!;
        comp.runQueuedChartInit();
    }

    private runQueuedChartInit(): void {
        this.zone.runOutsideAngular(() => {
            const awaitingApexMount = this.buildChartOptions();
            this.zone.run(() => {
                this._cdr.markForCheck();
                if (!awaitingApexMount) {
                    this.releaseChartInitSlot();
                } else {
                    setTimeout(() => {
                        if (this.initQueued && !this.chartRenderedEmitted) {
                            this.releaseChartInitSlot();
                        }
                    }, 8000);
                }
            });
        });
    }

    private releaseChartInitSlot(): void {
        this.initQueued = false;
        DynamicChartsComponent.initRunning = false;
        const gap = this.from === 'Tracker'
            ? DynamicChartsComponent.TRACKER_INIT_GAP_MS
            : DynamicChartsComponent.INIT_GAP_MS;
        setTimeout(
            () => DynamicChartsComponent.pumpInitQueue(),
            gap,
        );
    }

    private buildChartOptions(): boolean {
        this.chartRenderedEmitted = false;
        if (!this.chartData) {
            console.warn('DynamicCharts: No chart data provided');
            return false;
        }

        if (!this.chartData.series && !this.chartData.labels) {
            console.warn('DynamicCharts: Chart data missing series and labels');
            return false;
        }

        this.ensureSeriesFromRawFlags();

        const key = `${this.chartData.id}::${JSON.stringify(this.chartData.labels)}::${JSON.stringify(this.chartData.series)}::${this.chartData.color}`;
        if (
            this.memoKey === key &&
            this.chartOptions &&
            Object.keys(this.chartOptions).length
        ) {
            this.reapplyHiddenSeriesAsync();
            return false;
        }
        this.memoKey = key;

        // Clear pinned points on chart rebuild
        this._pinnedMap.clear();
        this.pinnedPointsArray = [];

        // PIE shortcut (kept explicit and fast)
        const sAny = this.chartData.series as ChartSeriesMap;
        const pieFirst = sAny?.default?.[0];
        if (pieFirst?.type === 'pie') {
            this.chartOptions = this.buildPieChartOptions(
                this.chartData.labels || [],
                pieFirst,
            );
            this.reapplyHiddenSeriesAsync();
            return true;
        }

        // Axis charts (line/bar)
        const labels = (this.chartData.labels ?? []).map((label: unknown) =>
            this.formattingPipe.transform(label),
        );
        const displayByLower = this.buildSeriesKeyDisplayByLower(
            this.chartData.series as ChartSeriesMap,
        );
        const seriesMap = this.normalizeSeriesKeys(
            this.chartData.series as ChartSeriesMap,
        );

        // Check if labels are numeric or dates
        this.areLabelsNumericOrDate = this.checkIfLabelsAreNumericOrDate(
            this.chartData.labels ?? [],
        );

        this.baseLabels = [...labels];
        this.baseSeriesMap = JSON.parse(JSON.stringify(seriesMap));

        const visibleLowerKeys = this.getVisibleSeriesNames(seriesMap);
        const visibleKeys = visibleLowerKeys.map(
            lk => displayByLower[lk] ?? lk,
        );
        this.seriesNames = visibleKeys;

        const initialSelection =
            (this.previousSelectedSeriesList?.length
                ? [...this.previousSelectedSeriesList]
                : this.selectedSeriesList) ?? [];
        this.selectedSeriesList = this.cleanSelection(
            initialSelection,
            visibleKeys,
        );
        if (!this.selectedSeriesList.length) {
            if (visibleKeys.length >= 2 && this.from !== 'dashboard') {
                this.selectedSeriesList = resolveDefaultSeriesSelection(
                    visibleKeys,
                    seriesMap,
                    10,
                );
            } else {
                const fallbackIndex =
                    this.from === 'dashboard' ? visibleKeys.length - 1 : 0;
                this.selectedSeriesList = [visibleKeys[fallbackIndex]];
            }
        }
        this.syncSeriesSelectControl();

        this.currentSeriesIndex =
            this.from === 'dashboard' ? Math.max(0, visibleKeys.length - 1) : 0;

        const firstSelKey = this.selectedSeriesList[0] ?? visibleKeys[0];
        const seriesArray = seriesMap[firstSelKey] ?? [];
        if (!seriesArray.length || !seriesArray[0]) {
            console.error(
                'DynamicCharts: Invalid series data for key:',
                firstSelKey,
                seriesMap,
            );
            return false;
        }

        const validatedSeries = seriesArray.map(s => ({
            name: s?.name ?? 'Series',
            data: Array.isArray(s?.data)
                ? s.data.map(v => this.coerceNumberOrNull(v))
                : [],
            type: (s?.type ?? 'line') as ChartType,
            originalType: s?.originalType,
        }));

        this.currentChartType = (validatedSeries[0]?.type ??
            'line') as ChartType;

        // APPLY the right chart config path (line vs bar)
        if (this.currentChartType === 'line') {
            this.chartOptions = this.buildLineChartOptions(
                labels,
                validatedSeries,
            );
        } else {
            this.chartOptions = this.buildBarChartOptions(
                labels,
                validatedSeries,
            );
        }

        this.reapplyHiddenSeriesAsync();

        // When multiple series exist, apply full selection so chart data matches select (e.g. expanded view / previousSelectedSeriesList)
        if (
            this.seriesNames.length >= 2 &&
            this.selectedSeriesList.length > 0
        ) {
            this.onMultiSeriesChange(this.selectedSeriesList);
        }
        return true;
    }

    // ---------- Chart builders (split, focused, minimal) ----------
    private notifyChartRendered(): void {
        if (this.chartRenderedEmitted) {
            return;
        }
        this.chartRenderedEmitted = true;
        this.chartRendered.emit();
        this.releaseChartInitSlot();
    }

    private bindChartPointerHandlers(chart: { el: HTMLElement }): void {
        const el = chart.el;
        if (!el) {
            return;
        }
        this.apexCtx = chart;
        this.captureXBounds(chart);

        el.addEventListener('mouseenter', () => {
            this.hovering = true;
        });
        el.addEventListener('mouseleave', () => {
            this.hovering = false;
            if (this.interactiveMode) {
                this.interactiveMode = false;
                this._cdr.detectChanges();
            }
        });

        el.addEventListener('mousedown', (ev: MouseEvent) => {
            this.pointerDownX = ev.clientX;
            this.pointerDownY = ev.clientY;
            this.pointerDidDrag = false;
        });
        el.addEventListener('mousemove', (ev: MouseEvent) => {
            if ((ev.buttons & 1) !== 1) {
                return;
            }
            if (
                Math.hypot(
                    ev.clientX - this.pointerDownX,
                    ev.clientY - this.pointerDownY,
                ) > 6
            ) {
                this.pointerDidDrag = true;
            }
        });

        el.addEventListener(
            'wheel',
            (ev: WheelEvent) => {
                if (this.isChartFullscreen) {
                    return;
                }
                if (!this.interactiveMode) {
                    return;
                }
                ev.preventDefault();
                ev.stopPropagation();
                this.zoomByWheel(ev, el);
            },
            { passive: false },
        );
    }

    private buildCommonBase(
        labels: string[],
        chartType: ChartType,
    ): Partial<ApexOptions> {
        const thresholdValue = resolveTrackerThreshold(
            this.chartData.threshold,
            this.chartData.thresholdType,
        );
        const formatValue = (val: any) => this.formattingPipe.transform(val);
        this.showToolbar = true;
        return {
            theme: {
                mode: this.isDarkMode ? 'dark' : 'light',
            },
            chart: {
                id: this.chartData.id,
                fontFamily: 'inherit',
                // Theme-aware (not 'inherit'): ApexCharts bakes foreColor into
                // legend/title inline styles; 'inherit' falls back to light #373d3f.
                foreColor: 'var(--fuse-text-default)',
                background: 'transparent',
                // Dashboard Token Usage fills a CSS-sized host (zoom/resize). Pixel
                // heights elsewhere stay explicit so tracker/report cards do not collapse.
                height: this.from === 'dashboard' ? '100%' : this.chartHeight,
                parentHeightOffset: this.from === 'dashboard' ? 0 : 15,
                redrawOnParentResize: this.from === 'dashboard',
                type: chartType,
                animations: { enabled: false },
                toolbar: {
                    show: this.showToolbar,
                    autoSelected: 'pan',
                    tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true,
                    },
                    export: {
                        csv: {
                            filename: 'high-resolution-chart',
                            columnDelimiter: ',',
                            headerCategory:
                                this.chartData?.xAxis ||
                                (this.chartData as any)?.dateColumn ||
                                'Category',
                            headerValue: 'Value',
                        },
                        svg: { filename: 'high-resolution-chart' },
                        png: { filename: 'high-resolution-chart' },
                    },
                },
                zoom: {
                    enabled: true,
                    type: 'x',
                    autoScaleYaxis: true,
                },
                events: {
                    mounted: (chart: any) => {
                        this.notifyChartRendered();
                        const el = chart.el;
                        if (!el) return;

                        this.fixApexToolbar(chart.el);

                        if (this.from !== 'Tracker') {
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    try {
                                        const chartId = this.chartData?.id;
                                        if (chartId) {
                                            ApexCharts.exec(
                                                String(chartId),
                                                'resize',
                                            );
                                        }
                                    } catch {
                                        /* ignore */
                                    }
                                });
                            });
                        }

                        this.bindChartPointerHandlers(chart);
                    },
                    updated: (chart: any) => {
                        this.fixApexToolbar(chart.el);
                        this.apexCtx = chart;
                        this.captureXBounds(chart);
                    },
                    legendClick: (
                        chartCtx: any,
                        seriesIndex: number,
                        opts: any,
                    ) => {
                        if (this.isChartFullscreen) {
                            return;
                        }
                        try {
                            const name =
                                opts?.w?.globals?.seriesNames?.[seriesIndex] ??
                                opts?.config?.series?.[seriesIndex]?.name;
                            if (typeof name === 'string' && name.length) {
                                if (this.hiddenSeries.has(name))
                                    this.hiddenSeries.delete(name);
                                else this.hiddenSeries.add(name);
                            }
                        } catch {}
                    },
                    click: (_event: any, _chartContext: any, opts: any) => {
                        if (this.isChartFullscreen) {
                            return;
                        }
                        // A click that concluded a pan drag must never pin.
                        if (this.pointerDidDrag) {
                            this.pointerDidDrag = false;
                            return;
                        }
                        // First click focuses the chart (enables wheel-zoom);
                        // subsequent clicks add/remove pinned points.
                        // detectChanges (not markForCheck): ApexCharts' click
                        // fires outside Angular's zone, so the indicator would
                        // otherwise not repaint until the next event.
                        if (!this.interactiveMode) {
                            this.interactiveMode = true;
                            this._cdr.detectChanges();
                            return;
                        }
                        const dpi = opts?.dataPointIndex;
                        if (dpi != null && dpi >= 0)
                            this.togglePinnedPoint(dpi);
                    },
                },
            },
            colors: generateColorVariants(
                this.chartData.color || this.c1Value,
                20,
            ),
            dataLabels: this.buildDataLabels(chartType, labels.length),
            grid: {
                show: true,
                borderColor: this.isDarkMode
                    ? 'rgba(148,163,184,0.18)'
                    : 'rgba(100,116,139,0.16)',
                strokeDashArray: 4,
                position: 'back',
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: true } },
                padding: { top: 10, right: 12, bottom: 8, left: this.isNarrowViewport ? 4 : 28 },
            },
            labels,
            states: {
                active: { filter: { type: 'none' } },
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                followCursor: true,
                theme: this.isDarkMode ? 'dark' : 'light',
                style: { fontSize: '13px' },
                x: {
                    formatter: (value: any) => {
                        return String(value ?? '');
                    },
                },
            },
            xaxis: { tooltip: { enabled: false }, categories: labels, axisBorder: { show: true }, axisTicks: { color: 'var(--fuse-border)' }, tickAmount: 6, labels: { show: true, style: { colors: 'var(--fuse-text-secondary)', fontSize: this.from === 'report' ? '14px' : '10px', fontWeight: this.from === 'report' ? '400' : '600' }, rotate: -45, rotateAlways: true, formatter: (value: string) => this.formatXAxisLabel(value) } },
            yaxis: { opposite: false, tickAmount: this.computeYAxisTickAmount(), labels: { offsetX: this.isFull ? 20 : (this.isNarrowViewport ? 2 : 10), style: { colors: 'var(--fuse-text-secondary)', fontSize: this.isNarrowViewport ? '11px' : '13px', fontWeight: '400' }, formatter: (value: number) => this.formatYAxis(value) } },
            title:
                this.from === 'report' || this.hideTitle
                    ? undefined
                    : this.buildTitleOptions(),
            annotations: this.from === 'Tracker' && thresholdValue != null ? { yaxis: [{ y: thresholdValue, borderColor: '#aa0000', borderWidth: 2, label: { text: `${this.chartData.thresholdType ?? ''}`, style: { color: '#333', background: '#E6F7FF', fontSize: '14px', fontWeight: '500' } } }] } : undefined,
        };
    }



    //     toolbar.classList.add('apex-toolbar-visible');

    //         '[ApexDebug] classList after add:',
    //         [...toolbar.classList]
    //     );
    // }

    /** Isolated data points — a non-null value whose neighbours on both sides are
     *  missing/null — render as a lonely dot on a line chart and are easy to miss.
     *  Emit a thin, series-coloured vertical annotation line at each so they stand
     *  out. Colours track the same palette as the series (`generateColorVariants`). */
    private buildIsolatedPointAnnotations(
        labels: string[],
        seriesData: ChartSeriesEntry[],
    ): Array<Record<string, unknown>> {
        const colors = generateColorVariants(
            this.chartData.color || this.c1Value,
            20,
        );
        const annotations: Array<Record<string, unknown>> = [];
        seriesData.forEach((series, si) => {
            const data = series.data ?? [];
            const color = colors[si % colors.length] || this.c1Value;
            for (let i = 0; i < data.length; i++) {
                if (data[i] == null) continue;
                const prev = i > 0 ? data[i - 1] : null;
                const next = i < data.length - 1 ? data[i + 1] : null;
                if (prev == null && next == null && labels[i] != null) {
                    annotations.push({
                        x: labels[i],
                        strokeDashArray: 0,
                        borderColor: color,
                        opacity: 0.55,
                    });
                }
            }
        });
        return annotations;
    }

    private buildLineChartOptions(
        labels: string[],
        seriesData: ChartSeriesEntry[],
    ): ApexOptions {
        const common = this.buildCommonBase(labels, 'line');
        const isolatedPointLines = this.buildIsolatedPointAnnotations(
            labels,
            seriesData,
        );
        return {
            ...common,
            annotations: {
                ...((common.annotations as Record<string, unknown>) ?? {}),
                ...(isolatedPointLines.length
                    ? { xaxis: isolatedPointLines }
                    : {}),
            },
            chart: {
                ...(common.chart as any),
                type: this.toRenderType('line'),
                dropShadow: this.buildLineDropShadow(true),
            },
            stroke: { curve: 'smooth', width: 3, lineCap: 'round' },
            fill: this.buildLineAreaFill(),
            markers: {
                size: 0,
                strokeWidth: 2,
                strokeColors: this.isDarkMode ? '#1a2332' : '#ffffff',
                hover: { size: 6, sizeOffset: 3 },
            },
            series: this.applyHiddenStateToSeries(seriesData).map(s => ({
                ...s,
                type: this.toRenderType('line'),
            })),
            tooltip: {
                ...(common.tooltip as any),
                followCursor: this.shouldFollowCursor(seriesData.length),
                custom: ({ dataPointIndex, w }: any) =>
                    this.buildCustomTooltip(w, dataPointIndex),
            },
        } as ApexOptions;
    }

    private buildBarChartOptions(
        labels: string[],
        seriesData: ChartSeriesEntry[],
    ): ApexOptions {
        // Aggregate bar data if more than 20 categories (similar to pie chart)
        const { labels: aggregatedLabels, series: aggregatedSeries } =
            this.aggregateBarData(labels, seriesData, 20);

        const common = this.buildCommonBase(aggregatedLabels, 'bar');
        this.showToolbar = false;
        return {
            ...common,
            chart: {
                ...(common.chart as any),
                type: 'bar',
                toolbar: { show: this.showToolbar },
            },
            xaxis: {
                ...(common.xaxis as any),
                tickAmount: undefined, // ✅ remove limit
            },

            plotOptions: this.buildBarPlotOptions(),
            fill: this.buildBarFill(),
            stroke: { show: false, width: 0 },
            series: this.applyHiddenStateToSeries(
                aggregatedSeries.map(s => ({
                    ...s,
                    type: 'bar' as ChartType,
                })),
            ),
            tooltip: {
                ...(common.tooltip as any),
                followCursor: this.shouldFollowCursor(aggregatedSeries.length),
                custom: ({ dataPointIndex, w }: any) =>
                    this.buildCustomTooltip(w, dataPointIndex),
            },
        } as ApexOptions;
    }

    private buildPieChartOptions(
        labels: any[],
        seriesEntry: ChartSeriesEntry,
    ): ApexOptions {
        const rawSeries = (seriesEntry.data || []).map(
            v => this.coerceNumberOrNull(v) ?? 0,
        );
        const { labels: pieLabels, series: pieSeries } = aggregatePieData(
            labels,
            rawSeries,
            10,
        );

        const total = pieSeries.reduce((a, b) => a + b, 0);
        this.showToolbar = false;

        return {
            theme: {
                mode: this.isDarkMode ? 'dark' : 'light',
            },
            chart: {
                id: this.chartData.id,
                type: 'donut',
                fontFamily: 'inherit',
                foreColor: 'var(--fuse-text-default)',
                background: 'transparent',
                height: this.chartHeight,
                dropShadow: {
                    enabled: true,
                    top: 2,
                    left: 0,
                    blur: 5,
                    opacity: this.isDarkMode ? 0.35 : 0.18,
                    color: '#000000',
                },
                toolbar: {
                    show: this.showToolbar,
                    tools: {
                        download: true,
                        reset: true,
                    },
                },
                events: {
                    mounted: () => {
                        this.notifyChartRendered();
                    },
                },
            },
            labels: pieLabels,
            series: pieSeries,
            colors: generateColorVariants(
                this.chartData.color || this.c1Value,
                pieLabels.length,
            ),
            // Gradient slice fills (like the bar/area charts) for depth. Always
            // shade toward 'dark': the light shade lightened slices toward white
            // and made light mode look faded. Light mode stays vibrant
            // (near-opaque, subtle gradient); dark mode keeps the softer, more
            // translucent look. Separators and data labels stay fully opaque.
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'dark',
                    type: 'diagonal1',
                    shadeIntensity: this.isDarkMode ? 0.45 : 0.3,
                    opacityFrom: this.isDarkMode ? 0.9 : 1,
                    opacityTo: this.isDarkMode ? 0.6 : 0.82,
                    stops: [0, 100],
                },
            },
            stroke: {
                width: 2,
                colors: [this.isDarkMode ? '#1a2332' : '#ffffff'],
            },
            dataLabels: {
                enabled: true,
                style: { fontSize: '12px', fontWeight: '600' },
                dropShadow: { enabled: true, top: 1, left: 0, blur: 2, opacity: 0.45 },
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    // Ring (donut) chart with a live center label: the hovered
                    // slice's name/value, falling back to the grand total.
                    donut: {
                        size: '62%',
                        labels: {
                            show: true,
                            name: {
                                fontSize: '13px',
                                fontWeight: 600,
                                color: this.isDarkMode ? '#8899a6' : '#64748b',
                                offsetY: -4,
                            },
                            value: {
                                fontSize: '20px',
                                fontWeight: 700,
                                color: this.isDarkMode ? '#f0f4f8' : '#1e293b',
                                offsetY: 6,
                                formatter: (val: string) =>
                                    this.formatYAxis(Number(val)),
                            },
                            total: {
                                show: true,
                                showAlways: false,
                                label: 'Total',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: this.isDarkMode ? '#8899a6' : '#64748b',
                                formatter: () => this.formatYAxis(total),
                            },
                        },
                    },
                },
            },
            states: { hover: { filter: { type: 'lighten', value: 0.06 } } },
            tooltip: {
                y: {
                    formatter: (val: number) => {
                        const percent = total ? (val / total) * 100 : 0;
                        return `${percent.toFixed(2)}%`;
                    },
                },
            },
            legend: this.getLegendConfig(total),
        };
    }

    // ---------- Tooltip builder kept readable ----------
    private shouldFollowCursor(seriesCount: number): boolean {
        return shouldFollowTooltipCursor(
            seriesCount,
            this.chartHeight,
            this.isNarrowViewport,
        );
    }

    private buildCustomTooltip(w: any, dataPointIndex: number): string {
        const config = w.config;
        // Prefer xaxis.categories when non-empty; when empty/missing use baseLabels (config can be [] at tooltip time)
        const categoriesFromConfig =
            (config.xaxis?.categories?.length
                ? config.xaxis.categories
                : null) ??
            this.baseLabels ??
            [];
        const fromGlobals = w.globals?.labels?.[dataPointIndex];
        const fromCategories = categoriesFromConfig[dataPointIndex];
        const rawCategory =
            fromCategories != null
                ? String(fromCategories)
                : fromGlobals != null
                  ? String(fromGlobals)
                  : '';
        const isOthers = rawCategory.trim() === 'others';
        const categoryLabel = rawCategory
            ? this.formatXAxisLabel(rawCategory)
            : rawCategory;

        const rows = collectTooltipRows(
            w,
            dataPointIndex,
            isOthers,
            value => this.formattingPipe.transform(value) ?? '0.00',
        );
        const layout = resolveTooltipLayout(
            rows.length,
            this.chartHeight,
            this.isNarrowViewport,
        );
        return buildSharedTooltipHtml(categoryLabel, rows, layout, {
            empty: this._transloco.translate(
                'No data available for this point',
            ),
            more: hidden =>
                this._transloco.translate('+{{count}} more', {
                    count: hidden,
                }),
        });
    }

    // ---------- Utilities ----------
    private formatXAxisLabel(value: string): string {
        if (!value) return '';
        let v: any = value;
        if (!isNaN(Number(v)) && !String(v).includes('-')) {
            const n = Number(v);
            v = n >= 1970 && n < 2030 ? v : n.toLocaleString('en-US');
        } else {
            const date = new Date(v);
            if (!isNaN(date.getTime()) && String(v).includes(':')) {
                const d = ('0' + date.getDate()).slice(-2);
                const m = ('0' + (date.getMonth() + 1)).slice(-2);
                const y = date.getFullYear();
                v = `${d}/${m}/${y}`;
            }
        }
        const maxLength = 18;
        return typeof v === 'string' && v.length > maxLength
            ? v.slice(0, maxLength) + '...'
            : String(v);
    }

    private formatYAxis(value: number): string {
        const abs = Math.abs(value);
        const sign = value < 0 ? '-' : '';
        if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)}T`;
        if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(2)}K`;
        return `${sign}${abs.toFixed(2)}`;
    }

    private checkIfLabelsAreNumericOrDate(labels: any[]): boolean {
        if (!labels || labels.length === 0) return false;

        // Check a sample of labels (first few) to determine if they're numeric or dates
        const sampleSize = Math.min(5, labels.length);
        let numericCount = 0;
        let dateCount = 0;

        for (let i = 0; i < sampleSize; i++) {
            const label = labels[i];
            if (label === null || label === undefined) continue;

            const labelStr = String(label).trim();

            // Check if it's a number
            const num = Number(labelStr);
            if (!isNaN(num) && isFinite(num) && labelStr !== '') {
                numericCount++;
                continue;
            }

            // Check if it's a date
            const date = new Date(labelStr);
            if (!isNaN(date.getTime())) {
                // Additional check: dates usually have specific patterns
                // Check for common date patterns or if it's a valid date string
                if (labelStr.includes('-') || labelStr.includes('/') || labelStr.includes(':') || 
                    labelStr.match(/\d{4}/) || date.getFullYear() >= 1900) {
                    dateCount++;
                    continue;
                }
            }
        }

        // If majority of sample labels are numeric or dates, consider them as such
        const totalChecked = numericCount + dateCount;
        return totalChecked >= Math.ceil(sampleSize * 0.6); // At least 60% should be numeric/date
    }

    private buildTitleOptions(): NonNullable<ApexOptions['title']> {
        const txt =
            this.chartTitle === 'null'
                ? ''
                : this.chartTitle || this.chartData.xAxis || '';
        const titleColor = this.isDarkMode ? '#e2e8f0' : '#333';
        return {
            text: txt,
            align: 'center',
            style: {
                fontWeight: this.from === 'report' ? 'bold' : '600',
                fontSize: this.from === 'report' ? '20px' : '14px',
                color: titleColor,
            },
            offsetY: 35,
        };
    }

    private getLegendConfig(total?: number): ApexOptions['legend'] {
        return {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
            // Font size is set via CSS (.apexcharts-legend-text) rather than
            // here: ApexCharts parseInt()s this value and mis-renders any rem
            // unit as 1px, so a rem-based match to the page type scale has to
            // live in the stylesheet. See dynamic-charts.component.scss.
            // Color must be explicit: theme.mode alone leaves legend at the
            // light-mode default (#373d3f), which is unreadable on dark cards.
            labels: {
                colors: 'var(--fuse-text-default)',
            },
            itemMargin: { horizontal: 5, vertical: 0 },
            markers: {
                width: 12,
                height: 12,
                radius: 12,
                offsetX: -5,
                offsetY: 0,
            },
            onItemClick: { toggleDataSeries: true },
            onItemHover: { highlightDataSeries: true },
            ...(total != null
                ? {
                      formatter: (seriesName: string, opts: any) => {
                          const value =
                              opts?.w?.globals?.series?.[opts.seriesIndex] ?? 0;
                          const percent = total
                              ? ((value / total) * 100).toFixed(2)
                              : '0.00';
                          return `${seriesName}: ${percent}%`;
                      },
                  }
                : {}),
        };
    }

    /** Rounded bar geometry shared by every bar render path (initial, toggle, sort).
     * Tip radius is 4px - a touch tighter than the design's smallest token
     * (--sai-radius-sm, 6px) so the bar caps read as a subtle round, not a pill. */
    private buildBarPlotOptions(): ApexPlotOptions {
        return {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 4,
                borderRadiusApplication: 'end',
                // Sit the value label just above each bar's tip, not centered
                // inside it (buildDataLabels supplies the -6 offsetY nudge).
                dataLabels: { position: 'top' },
            },
        };
    }

    /** Subtle vertical gradient: the bar stays solid and grounded at its base
     * (full opacity) and softens just slightly toward the top, giving a gentle
     * sense of light without a heavy, faded look. Kept understated on purpose -
     * a strong shade read as "too much" and an airy base looked upside down. */
    private buildBarFill(): ApexFill {
        return {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'vertical',
                shadeIntensity: 0.15,
                opacityFrom: this.isDarkMode ? 0.9 : 0.92,
                opacityTo: 1,
                stops: [0, 100],
            },
        };
    }

    /** Brand-tinted gradient under line charts: clearly visible just beneath
     * the stroke, then fading to fully transparent at the baseline so the lower
     * area reads as empty. The strong top-to-bottom contrast is what makes it
     * read as a deliberate gradient rather than a flat, uniform tint. */
    private buildLineAreaFill(): ApexFill {
        return {
            type: 'gradient',
            gradient: {
                shade: this.isDarkMode ? 'dark' : 'light',
                type: 'vertical',
                shadeIntensity: 0.4,
                opacityFrom: this.isDarkMode ? 0.5 : 0.42,
                opacityTo: 0,
                // Push the fade's midpoint high (85%) so most of the drop-off
                // happens in the upper band - the color stays present near the
                // line and the bottom stays fully transparent.
                stops: [0, 85],
            },
        };
    }

    /** ApexCharts only fills an area under 'area'-type series/charts, but every
     * other piece of this component (toggle detection, data-label density,
     * series maps) tracks the simpler internal 'line'/'bar' pair. These two
     * helpers are the ONLY place that translates between the two - apply
     * toRenderType() at the point a series/chart config is handed to
     * ApexCharts, and fromRenderType() when reading a live apex value back to
     * decide internal state (e.g. what the toggle button does next). */
    private toRenderType(type: ChartType): ChartType {
        // line -> area (gradient fill); pie -> donut (ring). Internal logic
        // still speaks 'line'/'pie'.
        if (type === 'line') {
            return 'area';
        }
        if (type === 'pie') {
            return 'donut';
        }
        return type;
    }

    private fromRenderType(type: ChartType): ChartType {
        if (type === 'area') {
            return 'line';
        }
        if (type === 'donut') {
            return 'pie';
        }
        return type;
    }

    /** Soft drop shadow for line charts; passed `false` on bar/sort paths to disable. */
    private buildLineDropShadow(enabled: boolean): ApexChart['dropShadow'] {
        return {
            enabled,
            top: 3,
            left: 0,
            blur: 4,
            opacity: this.isDarkMode ? 0.28 : 0.16,
            color: this.chartData?.color || this.c1Value,
        };
    }

    /** Fewer, cleaner y-axis gridlines on short/embedded charts (e.g. the
     * dashboard's ~240px tiles) where ApexCharts' auto tick count otherwise
     * crowds 6-7 labels into too little vertical space. Taller/full-page
     * charts keep more ticks since they have the room for finer granularity. */
    /** Phone-width viewport — used to reclaim y-axis label footprint so the plot
     *  gets more width on small screens (labels/offset otherwise eat ~90px). */
    private get isNarrowViewport(): boolean {
        return typeof window !== 'undefined' && window.innerWidth <= 640;
    }

    private computeYAxisTickAmount(): number {
        const height = this.chartHeight || 400;
        if (height <= 280) return 4;
        if (height <= 400) return 5;
        return 6;
    }

    private dataLabelStep = 1;

    private computeLabelStep(chartType: ChartType, count: number): number {
        if (chartType === 'bar' && count > 40)
            return Math.max(2, Math.ceil(count / 15));
        if (chartType === 'line' && count > 50)
            return Math.max(2, Math.ceil(count / 12));
        return 1;
    }

    private buildDataLabels(
        chartType: ChartType,
        totalPoints: number,
    ): ApexDataLabels {
        this.dataLabelStep = this.computeLabelStep(chartType, totalPoints);
        // Bars: with position:'top' ApexCharts anchors the label on the tip
        // (half inside the bar), so lift it by roughly a label-height to float
        // it fully above the bar with a small gap.
        const offsetY = chartType === 'bar' ? -32 : -4;
        return {
            enabled: this.isDataLabelsVisible,
            offsetY,
            background: { enabled: false },
            style: {
                fontSize: '12px',
                fontWeight: '600',
                colors: [this.isDarkMode ? '#cbd5e1' : '#475569'],
            },
            formatter: (val: any, opts?: any) => {
                if (!this.isDataLabelsVisible) return '';
                const idx = opts?.dataPointIndex ?? 0;
                if (this.dataLabelStep > 1 && idx % this.dataLabelStep !== 0)
                    return '';
                const n = this.coerceNumberOrNull(val);
                if (n == null || n === 0) return '';
                return this.formatShortNoDecimals(n);
            },
        };
    }

    private formatShortNoDecimals(value: any): string {
        const num =
            typeof value === 'number' ? value : this.coerceNumberOrNull(value);
        if (num == null || !Number.isFinite(num)) return '';
        const sign = num < 0 ? '-' : '';
        const abs = Math.abs(num);
        if (abs >= 1e12) return sign + Math.round(abs / 1e12) + 'T';
        if (abs >= 1e9) return sign + Math.round(abs / 1e9) + 'B';
        if (abs >= 1e6) return sign + Math.round(abs / 1e6) + 'M';
        if (abs >= 1e3) return sign + Math.round(abs / 1e3) + 'K'; // 👈 FIXED
        return sign + Math.round(abs).toString();
    }

    // ---------- Visibility helpers (kept simple) ----------
    private applyHiddenStateToSeries(
        series: ChartSeriesEntry[],
    ): ChartSeriesEntry[] {
        // Drop fully-flat (all-zero) lines so they don't clutter the chart or
        // legend; per-series hide state for the rest is managed via the
        // ApexCharts API. This is the single choke point every series-setting
        // path routes through.
        return this.filterFlatZeroSeries(series);
    }

    /** True when a series carries at least one meaningful (non-null, non-zero) value. */
    private seriesHasSignal(s: ChartSeriesEntry): boolean {
        return (
            Array.isArray(s?.data) &&
            s.data.some((v) => {
                const n = this.coerceNumberOrNull(v);
                return n != null && n !== 0;
            })
        );
    }

    /** Removes series whose values are entirely zero/blank. Guarded so a chart
     * is never left empty: a single-series input, or one where every series is
     * all-zero, is returned unchanged. */
    private filterFlatZeroSeries(
        series: ChartSeriesEntry[],
    ): ChartSeriesEntry[] {
        if (!Array.isArray(series) || series.length <= 1) return series;
        const withSignal = series.filter(s => this.seriesHasSignal(s));
        return withSignal.length ? withSignal : series;
    }

    private reapplyHiddenSeriesAsync(): void {
        const id = this.chartOptions?.chart?.['id'];
        if (!id || this.hiddenSeries.size === 0) return;
        const chartId = String(id);
        const names = Array.from(this.hiddenSeries);
        requestAnimationFrame(() => {
            names.forEach((name) => {
                try {
                    ApexCharts.exec(chartId, 'hideSeries', name);
                } catch {}
            });
        });
    }

    // ---------- UI actions (kept API-compatible) ----------

    /** Zoom In / Out / Reset are rendered as custom buttons in the unified tools
     * pill (see template) instead of the native ApexCharts toolbar, which is
     * hidden off-screen via SCSS. Rather than reimplement axis math, each button
     * delegates to the corresponding hidden native icon by dispatching a click -
     * reusing ApexCharts' exact zoom/pan/reset behaviour. */
    private triggerNativeToolbarIcon(selector: string): void {
        const host = (this.chartWrapper as any)?.nativeElement as
            | HTMLElement
            | undefined;
        const icon = host?.querySelector(selector) as HTMLElement | null;
        icon?.click();
    }

    zoomInChart(): void {
        this.triggerNativeToolbarIcon('.apexcharts-zoomin-icon');
    }

    zoomOutChart(): void {
        this.triggerNativeToolbarIcon('.apexcharts-zoomout-icon');
    }

    resetZoomChart(): void {
        this.triggerNativeToolbarIcon('.apexcharts-reset-icon');
    }

    openColorPicker() {
        this.matTooltipDisabled = true;
        this.colorPickerInput.nativeElement.click();
    }

    onColorChange(value: any): void {
        const newColor = this.sanitizeColor(value);
        this.chartData.color = newColor;
        this.updatePinnedAnnotations();
        // Update ApexCharts safely
        setTimeout(() => {
            this.updateOptions({
                colors: generateColorVariants(newColor, 20),
            });
        });

        this.changeTrackerColor.emit(newColor);
    }

    toggleDataLabelsVisibility(): void {
        this.isDataLabelsVisible = !this.isDataLabelsVisible;
        const type: ChartType = this.fromRenderType(
            (this.chartOptions.chart?.type as ChartType) ||
                this.currentChartType ||
                'line',
        );
        const categories =
            (this.chartOptions.xaxis as any)?.categories ??
            this.chartData.labels ??
            [];
        this.updateOptions({
            dataLabels: this.buildDataLabels(type, categories.length),
        });
        this.reapplyHiddenSeriesAsync();
    }


    toggleChartType(): void {
        const currentSeries =
            (this.chartOptions.series as ChartSeriesEntry[]) ?? [];
        if (!currentSeries.length) return;
        this.currentChartType = this.fromRenderType(
            currentSeries[0].type as ChartType,
        );
        const target: ChartType =
            this.currentChartType === 'line' ? 'bar' : 'line';

        if (this.isSorted && target === 'line') {
            this.isSorted = false;
            this.sortedCategories = null;
        }

        this.currentChartType = target;

        const originalCategories = [...this.baseLabels];
        const seriesMap = this.baseSeriesMap;
        // Multi-group series: append the metric ("- total_net_sales") only when
        // more than one metric exists; otherwise the group key alone (see makeSeriesNamer).
        const nameFor = makeSeriesNamer(this.selectedSeriesList, seriesMap);
        let baseSeries: ChartSeriesEntry[] = this.selectedSeriesList.flatMap(
            key =>
                (seriesMap[key] ?? []).map((s, idx) => ({
                    ...s,
                    name: nameFor(key, s.name, idx),
                })),
        );
        baseSeries = this.coerceSeriesEntries(baseSeries);

        let categories = originalCategories;
        let updatedSeries: ChartSeriesEntry[] = baseSeries.map(s => ({
            ...s,
            type: this.toRenderType(target),
        }));

        if (target === 'bar') {
            const aggregated = this.aggregateBarData(
                originalCategories,
                updatedSeries,
                20,
            );
            categories = aggregated.labels;
            updatedSeries = aggregated.series.map(s => ({
                ...s,
                type: this.toRenderType(target),
            }));
        }

        this.updateOptions({
            chart: {
                ...(this.chartOptions.chart ?? {}),
                type: this.toRenderType(target),
                dropShadow: this.buildLineDropShadow(target === 'line'),
            },
            series: this.applyHiddenStateToSeries(updatedSeries),
            plotOptions: target === 'bar' ? this.buildBarPlotOptions() : {},
            fill:
                target === 'bar'
                    ? this.buildBarFill()
                    : this.buildLineAreaFill(),
            xaxis: {
                ...(this.chartOptions.xaxis ?? {}),
                categories: [...categories],
            },
            stroke:
                target === 'line'
                    ? { curve: 'smooth', width: 3, lineCap: 'round' }
                    : { show: false, width: 0 },
            markers:
                target === 'line'
                    ? {
                          size: 0,
                          strokeWidth: 2,
                          strokeColors: this.isDarkMode ? '#1a2332' : '#ffffff',
                          hover: { size: 6, sizeOffset: 3 },
                      }
                    : { size: 0 },
            dataLabels: this.buildDataLabels(target, categories.length),
        });

        this.reapplyHiddenSeriesAsync();
    }

    toggleSelectAll(): void {
        this.isAllSelected = !this.isAllSelected;
        this.selectedSeriesList = this.isAllSelected
            ? [...this.seriesNames]
            : [];
        this.onMultiSeriesChange(this.selectedSeriesList);
    }

    onMultiSeriesChange(seriesNames: string[]): void {
        this.isSorted = false;
        this.sortedCategories = null;
        if (this.seriesNames.length < 2) return;
        const visible = this.seriesNames;
        this.selectedSeriesList = (seriesNames ?? []).filter(n =>
            visible.some(v => v === n),
        );
        this.isAllSelected = this.selectedSeriesList.length === visible.length;

        const seriesMap = this.normalizeSeriesKeys(
            this.chartData.series as ChartSeriesMap,
        );
        const nameFor = makeSeriesNamer(this.selectedSeriesList, seriesMap);
        let selectedSeriesData: ChartSeriesEntry[] = this.selectedSeriesList.flatMap(key => (seriesMap[key] ?? []).map((s, idx) => ({ ...s, name: nameFor(key, s.name, idx), type: this.toRenderType(this.currentChartType) })));
        selectedSeriesData = this.coerceSeriesEntries(selectedSeriesData);

        const categories = (this.chartData.labels ?? []).map((label: any) => this.formattingPipe.transform(label));

        // Apply aggregation for bar charts
        let finalCategories = categories;
        let finalSeriesData: ChartSeriesEntry[] = selectedSeriesData;
        if (this.currentChartType === 'bar') {
            const aggregated = this.aggregateBarData(
                categories,
                selectedSeriesData,
                20,
            );
            finalCategories = aggregated.labels;
            finalSeriesData = aggregated.series.map(s => ({
                ...s,
                type: (s.type ?? this.currentChartType) as ChartType,
            }));
        }

        const catsLen = finalCategories.length;

        this.updateOptions({
            series: this.applyHiddenStateToSeries(finalSeriesData),
            xaxis: {
                ...(this.chartOptions.xaxis ?? {}),
                categories: [...finalCategories],
            },
            legend: this.getLegendConfig(),
            tooltip: {
                ...(this.chartOptions.tooltip ?? {}),
                enabled: true,
                shared: true,
                intersect: false,
                followCursor: this.shouldFollowCursor(finalSeriesData.length),
            },
            dataLabels: this.buildDataLabels(this.currentChartType, catsLen),
        });
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }

    goToPreviousSeries(): void {
        if (this.currentSeriesIndex > 0) {
            this.currentSeriesIndex--;
            this.updateSeriesByIndex();
        }
    }
    goToNextSeries(): void {
        if (this.currentSeriesIndex < this.seriesNames.length - 1) {
            this.currentSeriesIndex++;
            this.updateSeriesByIndex();
        }
    }

    updateSeriesByIndex(): void {
        const seriesMap = this.normalizeSeriesKeys(
            this.chartData.series as ChartSeriesMap,
        );
        const key = this.seriesNames[this.currentSeriesIndex];
        this.selectedSeriesList = [key];
        this.syncSeriesSelectControl();
        const categories = (this.chartData.labels ?? []).map((label: any) => this.formattingPipe.transform(label));

        const seriesData = this.coerceSeriesEntries(seriesMap[key] ?? []);

        // Apply aggregation for bar charts
        let finalCategories = categories;
        let finalSeriesData: ChartSeriesEntry[] = seriesData.map(s => ({
            ...s,
            type: this.toRenderType(this.currentChartType),
        }));
        if (this.currentChartType === 'bar') {
            const aggregated = this.aggregateBarData(
                categories,
                seriesData,
                20,
            );
            finalCategories = aggregated.labels;
            finalSeriesData = aggregated.series.map(s => ({
                ...s,
                type: (s.type ?? this.currentChartType) as ChartType,
            }));
        }

        const catsLen = finalCategories.length;
        this.updateOptions({
            series: this.applyHiddenStateToSeries(finalSeriesData),
            xaxis: {
                ...(this.chartOptions.xaxis ?? {}),
                categories: [...finalCategories],
            },
            dataLabels: this.buildDataLabels(this.currentChartType, catsLen),
        });
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }

    sortSeries(order: 'asc' | 'desc'): void {
        this.currentChartType = 'bar';
        if (!this.selectedSeriesList.length) return;
        if (this.currentChartType !== 'bar') this.currentChartType = 'bar';

        // Get original series data from chartData (same naming as onMultiSeriesChange / toggleChartType)
        const seriesMap = this.normalizeSeriesKeys(
            this.chartData.series as ChartSeriesMap,
        );
        const nameFor = makeSeriesNamer(this.selectedSeriesList, seriesMap);
        const allSeries = this.coerceSeriesEntries(
            this.selectedSeriesList.flatMap(key =>
                (seriesMap[key] ?? []).map((s, idx) => ({
                    ...s,
                    name: nameFor(key, s.name, idx),
                })),
            ),
        );

        const originalLabels = (this.chartData.labels ?? []).map((label: any) => this.formattingPipe.transform(label));

        // First apply aggregation to get top 20 + Others
        const { labels: aggregatedLabels, series: aggregatedSeries } =
            this.aggregateBarData(originalLabels, allSeries, 20);

        // Now sort the aggregated data
        const sumPerCategory = aggregatedLabels.map((_, i) =>
            aggregatedSeries.reduce((acc, s) => {
                const v = s.data?.[i];
                const n = Number(v);
                return acc + (Number.isFinite(n) ? n : 0);
            }, 0)
        );

        const paired = aggregatedLabels.map((label, i) => ({
            label,
            value: sumPerCategory[i],
            originalIndex: i,
        }));
        paired.sort((a, b) =>
            order === 'asc' ? a.value - b.value : b.value - a.value,
        );
        this.sortedCategories = paired.map(p => p.label);

        const sortedSeries = aggregatedSeries.map((s) => {
            const newData = paired.map((p) => {
                const originalIndex = p.originalIndex;
                const v = s.data?.[originalIndex];
                return v == null ? null : Number(v);
            });

            return {
                ...s,
                data: newData,
                type: 'bar' as ChartType, // 🔥 FORCE SYNC
            };
        });

        this.isSorted = true;
        this.sortOrder = order;
        this.updateOptions({
            series: this.applyHiddenStateToSeries(sortedSeries),
            chart: {
                ...(this.chartOptions.chart ?? {}),
                type: 'bar',
                dropShadow: this.buildLineDropShadow(false),
            },
            xaxis: {
                ...(this.chartOptions.xaxis ?? {}),
                categories: [...(this.sortedCategories ?? [])],
            },
            plotOptions: this.buildBarPlotOptions(),
            fill: this.buildBarFill(),
            stroke: { show: false, width: 0 },
        });
        this.sortChange.emit(order);
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }

    // ---------- Export / download helpers ----------
    downloadPng(chart: any): void {
        if (chart.chart.id) {
            ApexCharts.exec(chart.chart.id, 'dataURI', { scale: 3 }).then(
                ({ imgURI }) => {
                    const a = document.createElement('a');
                    a.href = imgURI;
                    const left = chart.title?.text
                        ? `${chart.title?.text}`
                        : 'Chart';
                    const right = chart.yaxis?.title?.text
                        ? ` - ${chart.yaxis?.title?.text}`
                        : '';
                    a.download = `${left}${right}`.trim() || 'Chart';
                    a.click();
                },
            );
        } else {
            this.downloadSvgAsPng(this.chartWrapper);
        }
    }
    downloadSvgAsPng(wrapper: HTMLElement, fileName = 'chart.png') {
        const svg = wrapper.querySelector('svg');
        if (!svg) {
            console.error('SVG not found.');
            return;
        }

        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgString);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            const png = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = png;
            a.download = fileName;
            a.click();
        };

        img.src = svgBase64;
    }

    downloadSvg(chart: any): void {
        ApexCharts.exec(chart.chart.id, 'dataURI', {
            scale: 3,
            format: 'svg',
        }).then(({ imgURI }) => {
            const base64Data = imgURI.split(',')[1];
            const svgText = atob(base64Data);
            const blob = new Blob([svgText], {
                type: 'image/svg+xml;charset=utf-8',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = chart.title?.text
                ? `${chart.title?.text}.svg`
                : 'chart.svg';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    }

    exportToCSV(): void {
        const chartType = this.chartOptions.chart?.type;
        const series = (this.chartOptions.series as any[]) ?? [];
        const categories: any[] =
            chartType === 'pie' || chartType === 'donut'
                ? (this.chartOptions.labels ?? [])
                : ((this.chartOptions.xaxis as any)?.categories ?? []);
        if (!series.length || !categories.length) return;
        const categoryLabel =
            this.chartData?.xAxis ||
            (this.chartData as any)?.dateColumn ||
            'Category';
        const header = [
            this.safeCSV(this.toTitleCase(categoryLabel)),
            ...series.map(s =>
                this.safeCSV(this.toTitleCase(String(s.name ?? ''))),
            ),
        ].join(',');
        const rows = categories.map((cat, i) => {
            const cells = series.map(s => this.safeCSV(s.data?.[i]));
            return [this.safeCSV(cat), ...cells].join(',');
        });
        const csv = [header, ...rows].join('\n');
        const blob = new Blob(['\uFEFF' + csv], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        this.downloadURI(
            url,
            `${this.chartData.title || this.chartOptions?.title?.['text'] || 'Chart'}.csv`,
        );
    }

    private downloadURI(uri: string, name: string): void {
        const link = document.createElement('a');
        link.download = name || 'Chart';
        link.href = uri;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /** Title case for export headers: removes hyphens/dashes and capitalizes each word. */
    private toTitleCase(str: string): string {
        if (!str || typeof str !== 'string') return '';
        const withSpaces = str.replace(/[-_]+/g, ' ').trim();
        return withSpaces

            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    private safeCSV(val: any): string {
        const s = val == null ? '' : String(val);
        const escaped = s.replace(/"/g, '""');
        return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
    }

    // ---------- Template trackBy helpers ----------
    trackByIndex(_: number, item: any): any {
        return item;
    }
    trackBySeriesName(_: number, name: string): string {
        return name;
    }

    /** Options for mat-select-search: series names as { name } objects. */
    get seriesOptionsForSelect(): { name: string }[] {
        return (this.seriesNames ?? []).map(name => ({ name }));
    }

    /** Keeps seriesSelectControl in sync when selectedSeriesList is set programmatically. */
    private syncSeriesSelectControl(): void {
        this.seriesSelectControl.setValue([...this.selectedSeriesList], {
            emitEvent: false,
        });
    }

    openChartTypeMenu(): void {
        if (this.chartTypeMenuTrigger) this.chartTypeMenuTrigger.openMenu();
    }
    getLimitedTitle(text: string, wordLimit: number = 10): string {
        const words = (text || '').trim().split(/\s+/);
        return words.length > wordLimit
            ? words.slice(0, wordLimit).join(' ') + '...'
            : text || '';
    }

    // ---------- small public helpers used by template ----------
    openSettings(chart: any): void {
        this.openTrackerSettings.emit({
            ...chart,
            selectedSeriesList: this.selectedSeriesList?.length
                ? this.selectedSeriesList
                : null,
        });
    }
    openDataTable(chart: any): void {
        this.openTrackerDataTable.emit(chart);
    }
    addToPageFunc(chart: any): void {
        this.addToPage.emit(chart);
    }
    expandChart(data: any): void {
        this.expandChartEvent.emit({
            ...data,
            _sortOrder: this.sortOrder,
            selectedSeriesList: this.selectedSeriesList?.length
                ? [...this.selectedSeriesList]
                : undefined,
        });
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }
    deleteChart(data: any): void {
        this.deleteChartEvent.emit(data);
    }
    filter(data: any): void {
        this.filterChartEvent.emit(data);
    }
    shareTracker(event: MouseEvent): void {
        event.stopPropagation();
        this.shareTrackerEvent.emit(event);
    }

    // ---------- report date controls ----------
    toggleReportDatePicker(): void {
        this.showReportDatePicker = !this.showReportDatePicker;
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }
    applyReportDateRange(): void {
        this.dateRangeChange.emit({
            startDate: this.reportStartDate,
            endDate: this.reportEndDate,
        });
        this.showReportDatePicker = false;
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }
    resetReportDateRange(): void {
        this.reportStartDate = null;
        this.reportEndDate = null;
        this.dateRangeChange.emit({ startDate: null, endDate: null });
        this.showReportDatePicker = false;
        this._cdr.markForCheck();
        this.reapplyHiddenSeriesAsync();
    }

    // ---------- Pinned points ----------
    togglePinnedPoint(dataPointIndex: number): void {
        if (this._pinnedMap.has(dataPointIndex)) {
            this._pinnedMap.delete(dataPointIndex);
            if (this.highlightedPointIndex === dataPointIndex) {
                this.highlightedPointIndex = null;
            }
        } else {
            const allSeries = (this.chartOptions.series as any[]) || [];
            const categories =
                (this.chartOptions.xaxis as any)?.categories ??
                this.baseLabels ??
                [];
            const rawCategory =
                categories[dataPointIndex] != null
                    ? String(categories[dataPointIndex])
                    : '';
            const categoryLabel = rawCategory
                ? this.formatXAxisLabel(rawCategory)
                : rawCategory;
            const colors = this.chartOptions.colors || [];
            const values: PinnedPoint['values'] = [];

            for (let idx = 0; idx < allSeries.length; idx++) {
                const val = allSeries[idx].data?.[dataPointIndex];
                if (val == null || val === '' || val === 0) continue;
                if (typeof val === 'number' && Number.isNaN(val)) continue;
                values.push({
                    name: allSeries[idx].name || `Series ${idx + 1}`,
                    value: this.formattingPipe.transform(val) ?? String(val),
                    color: (colors as string[])[idx] || '#666',
                });
            }

            if (values.length) {
                this._pinnedMap.set(dataPointIndex, {
                    dataPointIndex,
                    category: categoryLabel,
                    rawCategory,
                    values,
                });
            }
        }
        this.pinnedPointsArray = Array.from(this._pinnedMap.values());
        this.updatePinnedAnnotations();
        this._cdr.detectChanges();
    }

    removePinnedPoint(dataPointIndex: number): void {
        this._pinnedMap.delete(dataPointIndex);
        if (this.highlightedPointIndex === dataPointIndex) {
            this.highlightedPointIndex = null;
        }
        this.pinnedPointsArray = Array.from(this._pinnedMap.values());
        this.updatePinnedAnnotations();
        this._cdr.detectChanges();
    }

    clearPinnedPoints(): void {
        this._pinnedMap.clear();
        this.pinnedPointsArray = [];
        this.highlightedPointIndex = null;
        this.updatePinnedAnnotations();
        this._cdr.detectChanges();
    }

    highlightPoint(dataPointIndex: number): void {
        if (this.highlightedPointIndex === dataPointIndex) {
            this.highlightedPointIndex = null;
            this.updatePinnedAnnotations();
            this._cdr.detectChanges();
            return;
        }

        this.highlightedPointIndex = dataPointIndex;
        this.updatePinnedAnnotations();

        this._cdr.detectChanges();
    }

    /** Converts a formatted x-axis category (e.g. "8.00") into the exact string
     * ApexCharts uses to locate an x-axis annotation. ApexCharts matches the
     * annotation `x` against its parsed axis labels (a number-like category
     * "8.00" is parsed to the label 8), so the annotation must be the STRING of
     * that parsed value ("8") - NOT the raw formatted string ("8.00" matches
     * nothing) and NOT a number (8 lands at the axis origin). This mismatch was
     * the regression: the pinned vertical line silently failed to render.
     * Non-numeric (category/date) labels are returned unchanged. */
    private toAnnotationX(rawCategory: string): string {
        const numeric = Number(String(rawCategory).replace(/,/g, ''));
        return String(rawCategory).trim() !== '' && Number.isFinite(numeric)
            ? String(numeric)
            : rawCategory;
    }

    private updatePinnedAnnotations(): void {
        const thresholdValue = resolveTrackerThreshold(
            this.chartData.threshold,
            this.chartData.thresholdType,
        );
        const yaxis =
            this.from === 'Tracker' && thresholdValue != null
                ? [
                      {
                          y: thresholdValue,
                          borderColor: '#aa0000',
                          borderWidth: 2,
                          label: {
                              text: `${this.chartData.thresholdType ?? ''}`,
                              style: {
                                  color: '#333',
                                  background: '#E6F7FF',
                                  fontSize: '14px',
                                  fontWeight: '500',
                              },
                          },
                      },
                  ]
                : undefined;
        const pinnedColor = this.chartData?.color || this.c1Value || '#6366f1';
        const highlightedColor = '#f59e0b';
        const xaxis = this.pinnedPointsArray.map((point) => {
            const isHighlighted =
                this.highlightedPointIndex === point.dataPointIndex;
            const color = isHighlighted ? highlightedColor : pinnedColor;
            return {
                x: this.toAnnotationX(point.rawCategory),
                borderColor: color,
                strokeDashArray: 0,
                label: {
                    text: '▼',
                    orientation: 'horizontal',
                    borderWidth: 0,
                    style: {
                        background: 'transparent',
                        color,
                        fontSize: '16px',
                        padding: { left: 4, right: 4, top: 0, bottom: 0 },
                    },
                },
            };
        });
        this.updateOptions({
            annotations: {
                yaxis,
                xaxis,
            },
        });
    }

    // ---------- small helpers ----------
    private formatShortNoDecimalsPublic(value: any): string {
        return this.formatShortNoDecimals(value);
    }

    private moveApexToolbar(): void {
        if (!this.apexToolbarHost) return;

        const tryMove = () => {
            const toolbar = document.querySelector(
                `#${this.chartData?.id} .apexcharts-toolbar`,
            ) as HTMLElement | null;

            if (!toolbar) return false;

            // Reset Apex inline positioning
            toolbar.style.position = 'static';
            toolbar.style.top = '';
            toolbar.style.right = '';
            toolbar.style.left = '';
            toolbar.style.transform = '';

            this.apexToolbarHost.nativeElement.appendChild(toolbar);
            return true;
        };

        // Try immediately
        if (tryMove()) return;

        // Retry once chart finishes rendering
        setTimeout(() => tryMove(), 0);
        setTimeout(() => tryMove(), 50);
        setTimeout(() => tryMove(), 150);
    }

    private aggregateBarData(
        labels: any[],
        seriesData: ChartSeriesEntry[],
        topN: number = 20,
    ): { labels: any[]; series: ChartSeriesEntry[] } {
        // If we have 20 or fewer labels, no aggregation needed
        if (labels.length <= topN) {
            return { labels, series: seriesData };
        }

        // Calculate sum per category across all series
        const sumPerCategory = labels.map((_, i) => {
            return seriesData.reduce((sum, series) => {
                const value = series.data?.[i];
                const numValue = this.coerceNumberOrNull(value) ?? 0;
                return sum + numValue;
            }, 0);
        });

        // Create pairs with label and total value
        const pairs = labels.map((label, i) => ({
            label,
            value: sumPerCategory[i],
            originalIndex: i,
        }));

        // Sort DESC by total value to find top N
        pairs.sort((a, b) => b.value - a.value);

        const top = pairs.slice(0, topN);
        const rest = pairs.slice(topN);

        // Restore original (chronological) order for display
        top.sort((a, b) => a.originalIndex - b.originalIndex);

        // Calculate "Others" sum for each series
        const othersData = seriesData.map((series) => {
            return rest.reduce((sum, p) => {
                const value = series.data?.[p.originalIndex];
                const numValue = this.coerceNumberOrNull(value) ?? 0;
                return sum + numValue;
            }, 0);
        });

        // Build final labels (top 20 + "Others" if needed)
        const finalLabels = top.map(p => p.label);
        if (rest.length > 0 && othersData.some(val => val > 0)) {
            finalLabels.push('Others');
        }

        // Build final series data
        const finalSeries = seriesData.map((series, seriesIndex) => {
            const newData = top.map((p) => {
                const originalIndex = p.originalIndex;
                return series.data?.[originalIndex] ?? null;
            });

            // Add "Others" data if needed
            if (rest.length > 0 && othersData[seriesIndex] > 0) {
                newData.push(othersData[seriesIndex]);
            }

            return {
                ...series,
                type: (series.type ?? 'bar') as ChartType,
                data: newData,
            };
        });

        return { labels: finalLabels, series: finalSeries };
    }
}

